import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { login, getTenantsConfig, getTenantsSAMLConfig } from '../services/authService';
import './Login.css';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField'; // Using TextField for better integration with InputAdornment
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Button from '@mui/material/Button'; // For a consistent MUI look
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert'; // For displaying errors
import { useUser } from '../contexts/UserContext'; // Import useUser
import { useTenant } from '../contexts/TenantContext'; // Import useTenant
import { UserManager } from 'oidc-client-ts';
import Divider from '@mui/material/Divider';

const LoginOnTenant = () => {
  const { t } = useTranslation();
  const { tenant_id } = useParams(); // Extract tenant_id from URL
  const queryParams = new URLSearchParams(window.location.search);
  const dev = queryParams.get('dev'); // Extract 'dev' parameter from the query string
  const [tenantUUID, setTenantUUID] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oidcConfigs, setOidcConfigs] = useState([]);
  const [samlConfigs, setSamlConfigs] = useState([]); // State to store SAML configurations
  const [loadingConfigs, setLoadingConfigs] = useState(true);
  const [errorConfigs, setErrorConfigs] = useState(null);
  const navigate = useNavigate();
  const { fetchUserData } = useUser(); // Get fetchUserData from context
  const { fetchTenantData } = useTenant(); // Get fetchTenantData from context

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password, tenantUUID); // Pass tenant_id to login function
      await fetchUserData();
      await fetchTenantData();

      if (!tenant_id) {
        console.error('Login: tenant_id is undefined. Cannot redirect to dashboard.');
        setError(t('login.loginFailed')); // Set a generic error message
        return;
      }

      console.log(`Login: Redirecting to /${tenant_id}/dashboard`);
      navigate(`/app/${tenant_id}/dashboard`); // Navigate to tenant-specific dashboard
    } catch (err) {
      if (err.response) {
        switch (err.response.status) {
          case 401:
            setError(t('login.loginFailedAuth'));
            break;
          case 429:
            const retryAfterHeader = err.response.headers['retry-after'] || err.response.headers['Retry-After'];
            let delaySeconds = null;
            if (retryAfterHeader) {
              if (!isNaN(parseFloat(retryAfterHeader)) && isFinite(retryAfterHeader)) {
                delaySeconds = parseInt(retryAfterHeader, 10);
              } else {
                const retryDate = new Date(retryAfterHeader);
                if (!isNaN(retryDate.getTime())) {
                  const nowDate = new Date();
                  delaySeconds = Math.round((retryDate.getTime() - nowDate.getTime()) / 1000);
                }
              }
            }
            if (delaySeconds && delaySeconds > 0) {
              setError(t('login.loginFailedRateLimit', { seconds: delaySeconds }));
            } else {
              setError(t('login.loginFailedRateLimitGeneric'));
            }
            break;
          default:
            setError(t('login.loginFailed'));
        }
      } else {
        // Network error or other issue not from a server response
        setError(t('login.loginFailed'));
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchOidcConfigs = async () => {
      let onlyEnabled = dev !== 'dev'; // If dev param is 'dev', fetch all configs, else only enabled ones

      console.log('Login: fetchOidcConfigs called with dev =', dev);
      console.log('Login: fetchOidcConfigs called with onlyEnabled =', onlyEnabled);

      try {
        const samlConfigs = await getTenantsSAMLConfig(tenant_id, onlyEnabled);
        setSamlConfigs(samlConfigs);
        
        const {tenantID, oidcConfigs} = await getTenantsConfig(tenant_id, onlyEnabled);
        console.log('Fetching OIDC configurations for tenant:', tenantID);
        console.log('OIDC Configurations Response:', oidcConfigs); // Log the response
        setOidcConfigs(oidcConfigs); // Ensure results is an array
        setTenantUUID(tenantID);
        console.log('OIDC Configurations Set:', oidcConfigs);
      } catch (error) {
        console.error('Error fetching OIDC configurations:', error);
        setErrorConfigs('');
      } finally {
        setLoadingConfigs(false);
      }
    };

    fetchOidcConfigs();
  }, [tenant_id]);

  useEffect(() => {
    const checkIfLoggedIn = async () => {
      console.log('Checking if user is already logged in...');
      const token = localStorage.getItem('access_token');
      if (token) {
        try {
          await fetchUserData();
          await fetchTenantData();
          const tenantData = JSON.parse(localStorage.getItem('tenantData'));
          const subdomainPrefix = tenantData?.subdomain_prefix;
          console.log('User is already logged in, redirecting to dashboard for tenant:', subdomainPrefix);
          if (subdomainPrefix) {
            navigate(`/app/${subdomainPrefix}/dashboard`, { replace: true });
          }
        } catch (error) {
          console.error('Error checking login status:', error);
        }
      }
    };

    checkIfLoggedIn();
  }, [fetchUserData, fetchTenantData, navigate]);

  const handleOidcLogin = (config) => {
    // Store the selected OIDC configuration in sessionStorage
    sessionStorage.setItem('selectedOidcConfig', JSON.stringify(config));

    const dynamicUserManager = new UserManager(config);
    dynamicUserManager.signinRedirect();
  };

  return (
    <Box 
      className="login-container" 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f0f2f5', // Optional: background color for the page
        paddingBottom: '60px' // Added padding at the bottom
      }}
    >
      <Box 
        className="login-form-wrapper"
        sx={{
          padding: { xs: 2, sm: 3, md: 4 },
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          width: '100%',
          maxWidth: '400px'
        }}
      >
        <Box className="login-logo-container" sx={{ textAlign: 'center', mb: 2 }}>
          <img 
            src="/logo_banane_transparent.png" 
            alt={t('login.logoAlt')}
            className="login-logo-image" 
            style={{ height: '60px' }} // Adjust size as needed
            onClick={() => navigate("/")} // Reload page on logo click
          />
        </Box>
        <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', fontWeight: 'medium' }}>
          [{tenant_id}] {t('login.loginTitle')}
        </Typography>

        {loadingConfigs ? (

          <Typography>Loading SAML configurations...</Typography>
        ) : errorConfigs ? (
          <Typography color="error">{errorConfigs}</Typography>
        ) : Array.isArray(samlConfigs) && samlConfigs.length > 0 ? samlConfigs.map((config) => (
          <>
            <Button
              key={config.name}
              variant="contained"
              color="secondary"
              onClick={() => {
                // Redirect to SAML endpoint
                window.location.href = config.url; // Assuming config has saml_login_url
              }}
              fullWidth
              sx={{ mt: 2, py: 1.5 }}
            >
              {t('login.loginWithProvider', { provider: config.name })}
            </Button>
          </>
        )) : (
          <Typography>No SAML configurations available.</Typography>
        )
        } 

        {loadingConfigs ? (
          <Typography>Loading OIDC configurations...</Typography>
        ) : errorConfigs ? (
          <Typography color="error">{errorConfigs}</Typography>
        ) : Array.isArray(oidcConfigs) ? oidcConfigs.map((config) => (
          <>
            <Button
              key={config.name}
              variant="contained"
              color="secondary"
              onClick={() => handleOidcLogin(config)}
              fullWidth
              sx={{ mt: 2, py: 1.5 }}
            >
              {t('login.loginWithProvider', { provider: config.name })}
            </Button>
          </>
        )) : (
          <Typography>No OIDC configurations available.</Typography>
        )}

        <Divider sx={{ p: 2 }}>{t('login.OR')}</Divider>
      
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleSubmit} className="login-form">
          <TextField
            label={t('login.emailLabel')}
            variant="outlined"
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('login.passwordLabel')}
            variant="outlined"
            type={showPassword ? 'text' : 'password'}
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            margin="normal"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('login.togglePasswordVisibility')}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <Button 
            type="submit" 
            variant="contained" 
            color="primary" 
            className="login-button" 
            disabled={loading}
            fullWidth
            sx={{ mt: 2, py: 1.5 }}
          >
            {loading ? t('login.loggingIn') : t('login.signInButton')}
          </Button>
        </form>
        
        
        
        {/* <div className="form-footer">
          <Link to="/forgot-password">{t('login.forgotPasswordLink')}</Link>
        </div> */}
      </Box>
    </Box>
  );
};

export default LoginOnTenant;
